/**
 * IDF Editor Component
 *
 * A Monaco-based code editor for EnergyPlus IDF files with:
 * - Syntax highlighting
 * - Error markers
 * - Auto-completion from IDD schema
 * - Hover documentation
 * - Integration with IDF store
 * - Theme sync with application dark/light mode
 */

import { useCallback, useEffect, useRef } from 'react';
import Editor, { type OnMount, type OnChange, loader } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import type * as Monaco from 'monaco-editor';
import { useIDFStore } from '../stores/idfStore';
import { useIDDStore } from '../stores/iddStore';
import { registerIDFLanguage, IDF_LANGUAGE_ID } from './idf-language';
import {
  registerIDFLanguageService,
  type LanguageServiceDisposables,
} from './idf-language-service';
import {
  registerValidationService,
  updateValidationMarkers,
  type ValidationServiceDisposables,
} from './idf-validation-service';
import { registerEnvelopThemes, getMonacoTheme } from './monaco-themes';
import { useTheme } from '@/theme';
import { useEditorRegistration } from './EditorContext';
import { useIsMobile } from '@/hooks';

// Configure Monaco loader to use local resources
loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs',
  },
});

interface IDFEditorProps {
  /** Initial content (optional, uses store if not provided) */
  initialContent?: string;
  /** Callback when content changes */
  onChange?: (value: string) => void;
  /** Whether the editor is read-only */
  readOnly?: boolean;
  /** Editor height (default: 100%) */
  height?: string;
  /** Whether to auto-parse on change (with debounce) */
  autoParse?: boolean;
  /** Debounce delay for auto-parse in ms (default: 500) */
  parseDelay?: number;
}

/**
 * Monaco-based IDF Editor component
 */
export function IDFEditor({
  initialContent,
  onChange,
  readOnly = false,
  height = '100%',
  autoParse = true,
  parseDelay = 500,
}: IDFEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const parseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const languageServiceRef = useRef<LanguageServiceDisposables | null>(null);
  const validationServiceRef = useRef<ValidationServiceDisposables | null>(null);

  // Theme context for syncing editor theme
  const { resolvedTheme } = useTheme();

  // Mobile detection for responsive font size
  const isMobile = useIsMobile();

  // Editor registration for EditorContext - use ref to avoid stale closure
  const editorRegistration = useEditorRegistration();
  const editorRegistrationRef = useRef(editorRegistration);
  editorRegistrationRef.current = editorRegistration;

  // Track if editor has been registered
  const hasRegisteredRef = useRef(false);

  // IDF Store state
  const rawContent = useIDFStore((state) => state.rawContent);
  const errors = useIDFStore((state) => state.errors);
  const document = useIDFStore((state) => state.document);
  const setRawContent = useIDFStore((state) => state.setRawContent);
  const parseContent = useIDFStore((state) => state.parseContent);

  // Use provided content or store content
  const content = initialContent ?? rawContent;

  /**
   * Handle editor mount
   */
  const handleEditorDidMount: OnMount = useCallback(
    (editorInstance, monacoInstance) => {
      editorRef.current = editorInstance;
      const typedMonaco = monacoInstance as typeof Monaco;
      monacoRef.current = typedMonaco;

      // Register IDF language
      registerIDFLanguage(typedMonaco);

      // Register Envelop themes (light and dark)
      registerEnvelopThemes(typedMonaco);

      // Register language service for auto-completion and hover
      // Use a getter function to always get the current schema from the store
      languageServiceRef.current = registerIDFLanguageService(typedMonaco, () => {
        const state = useIDDStore.getState();
        return state.isLoaded ? state.schema : null;
      });

      // Register validation service for code lenses and go-to-definition
      validationServiceRef.current = registerValidationService(
        typedMonaco,
        () => useIDFStore.getState().document,
        () => {
          const state = useIDDStore.getState();
          return state.isLoaded ? state.schema : null;
        },
        // Sync check: ensure editor content matches store content
        // This prevents showing CodeLens at wrong positions during editing
        () => {
          const model = editorInstance.getModel();
          if (!model) return false;
          const idfState = useIDFStore.getState();
          return model.getValue() === idfState.rawContent;
        }
      );

      // Set editor theme based on current app theme
      typedMonaco.editor.setTheme(getMonacoTheme(resolvedTheme));

      // Configure editor options with responsive font size
      editorInstance.updateOptions({
        fontSize: isMobile ? 12 : 14,
        fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
        lineNumbers: 'on',
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        wordWrap: 'off',
        tabSize: 2,
        insertSpaces: true,
        renderWhitespace: 'selection',
        bracketPairColorization: { enabled: false },
        guides: {
          indentation: true,
          bracketPairs: false,
        },
        // Enable suggestions
        quickSuggestions: true,
        suggestOnTriggerCharacters: true,
        // Accessibility options
        accessibilitySupport: 'auto',
        ariaLabel: 'IDF Code Editor',
      });

      // Register editor with EditorContext for external access
      editorRegistrationRef.current.registerEditor(editorInstance, typedMonaco);
      hasRegisteredRef.current = true;

      // Focus the editor (skip on mobile)
      if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        editorInstance.focus();
      }
    },
    [resolvedTheme, isMobile]
  );

  /**
   * Sync editor theme when app theme changes
   */
  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;

    monaco.editor.setTheme(getMonacoTheme(resolvedTheme));
  }, [resolvedTheme]);

  /**
   * Update editor font size when mobile state changes
   */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.updateOptions({
      fontSize: isMobile ? 12 : 14,
    });
  }, [isMobile]);

  /**
   * Backup registration - in case onMount doesn't fire properly on some devices
   */
  useEffect(() => {
    // Check every 500ms if editor is ready but not registered
    const checkInterval = setInterval(() => {
      if (editorRef.current && monacoRef.current && !hasRegisteredRef.current) {
        editorRegistrationRef.current.registerEditor(editorRef.current, monacoRef.current);
        hasRegisteredRef.current = true;
      }
      // Clear interval once registered
      if (hasRegisteredRef.current) {
        clearInterval(checkInterval);
      }
    }, 500);

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  /**
   * Handle content changes
   */
  const handleChange: OnChange = useCallback(
    (value) => {
      const newValue = value ?? '';

      // Update raw content in store
      setRawContent(newValue);

      // Call external onChange handler
      onChange?.(newValue);

      // Schedule auto-parse if enabled
      if (autoParse) {
        if (parseTimeoutRef.current) {
          clearTimeout(parseTimeoutRef.current);
        }
        parseTimeoutRef.current = setTimeout(() => {
          parseContent(newValue);
        }, parseDelay);
      }
    },
    [setRawContent, onChange, autoParse, parseDelay, parseContent]
  );

  /**
   * Update error markers when errors change
   */
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    // Convert parse errors to Monaco markers
    const markers: editor.IMarkerData[] = errors.map((error) => ({
      severity:
        error.severity === 'error' ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
      message: error.message,
      startLineNumber: error.line,
      startColumn: error.column ?? 1,
      endLineNumber: error.line,
      endColumn: error.column ? error.column + 10 : model.getLineMaxColumn(error.line),
    }));

    monaco.editor.setModelMarkers(model, 'idf-parser', markers);
  }, [errors]);

  /**
   * Update validation markers when document or schema changes
   */
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    if (!editor || !monaco) return;

    const model = editor.getModel();
    if (!model) return;

    // Check if Monaco content matches the serialized document
    // If they differ (user is mid-edit), skip updating markers to avoid wrong line positions
    const monacoContent = model.getValue();
    if (monacoContent !== content) {
      // Content is out of sync - clear validation markers to avoid showing them on wrong lines
      monaco.editor.setModelMarkers(model, 'idf-validation', []);
      return;
    }

    // Get current schema
    const iddState = useIDDStore.getState();
    const schema = iddState.isLoaded ? iddState.schema : null;

    // Update validation markers
    updateValidationMarkers(monaco, editor, document, schema);
  }, [document, content]);

  /**
   * Update editor content when external content changes
   */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const model = editor.getModel();
    if (!model) return;

    // Only update if content differs (avoid cursor jump)
    if (model.getValue() !== content) {
      const position = editor.getPosition();
      model.setValue(content);
      if (position) {
        editor.setPosition(position);
      }
    }
  }, [content]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Clear parse timeout
      if (parseTimeoutRef.current) {
        clearTimeout(parseTimeoutRef.current);
      }
      // Dispose language service providers
      if (languageServiceRef.current) {
        languageServiceRef.current.completion.dispose();
        languageServiceRef.current.hover.dispose();
      }
      // Dispose validation service providers
      if (validationServiceRef.current) {
        validationServiceRef.current.codeLens.dispose();
        validationServiceRef.current.definition.dispose();
      }
      // Unregister editor from EditorContext
      editorRegistrationRef.current.unregisterEditor();
      hasRegisteredRef.current = false;
    };
  }, []);

  // Get the appropriate theme name
  const monacoTheme = getMonacoTheme(resolvedTheme);

  return (
    <Editor
      height={height}
      language={IDF_LANGUAGE_ID}
      value={content}
      onChange={handleChange}
      onMount={handleEditorDidMount}
      options={{
        readOnly,
        automaticLayout: true,
      }}
      theme={monacoTheme}
      loading={
        <div
          className="flex h-full items-center justify-center bg-background text-muted-foreground"
          role="status"
          aria-label="Loading editor"
        >
          Loading editor...
        </div>
      }
    />
  );
}

export default IDFEditor;
